import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    Stack,
    Divider,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    InputAdornment,
    Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RepeatIcon from '@mui/icons-material/Repeat';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const ScheduleMeet = ({ onBack, onSave }) => {
    const [meetingData, setMeetingData] = useState({
        title: '',
        attendees: '',
        startDate: '2026-03-13',
        startTime: '04:30 PM',
        endDate: '2026-03-13',
        endTime: '05:00 PM',
        location: '',
        details: '',
        id: 'meet-' + Math.random().toString(36).substring(2, 9)
    });

    const handleChange = (field, value) => {
        setMeetingData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        if (!meetingData.title.trim()) {
            alert('Please add a title for the meeting.');
            return;
        }
        if (onSave) {
            onSave(meetingData);
        }
        onBack();
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ p: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #edebe9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ bgcolor: '#4b53bc', color: '#fff', p: 0.5, borderRadius: 1, display: 'flex' }}>
                        <EventIcon fontSize="small" />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px' }}>
                        New meeting <Typography component="span" sx={{ color: '#616161', fontSize: '14px', ml: 1, fontWeight: 400 }}>Details</Typography>
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" sx={{ bgcolor: '#4b53bc', '&:hover': { bgcolor: '#3b43a2' }, px: 3 }} onClick={handleSave}>
                        Save
                    </Button>
                    <Button variant="outlined" sx={{ color: '#242424', borderColor: '#d1d1d1' }} onClick={onBack}>
                        Close
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Main Form Area */}
                <Box sx={{ flex: 1, p: 4, overflowY: 'auto' }}>
                    <Typography variant="caption" sx={{ color: '#616161', display: 'flex', alignItems: 'center', mb: 3 }}>
                        Time zone: (UTC) Coordinated Universal Time <ArrowForwardIosIcon sx={{ fontSize: 10, ml: 0.5 }} />
                    </Typography>

                    <Stack spacing={3} sx={{ maxWidth: '800px' }}>
                        {/* Title */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <EditIcon sx={{ color: '#616161', mt: 1, fontSize: 20 }} />
                            <TextField
                                fullWidth
                                placeholder="Add title"
                                variant="standard"
                                value={meetingData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                InputProps={{ disableUnderline: false, style: { fontSize: '18px' } }}
                            />
                        </Box>

                        {/* Attendees */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <PersonOutlineIcon sx={{ color: '#616161', mt: 1, fontSize: 20 }} />
                            <TextField
                                fullWidth
                                placeholder="Enter name or e-mail"
                                variant="filled"
                                value={meetingData.attendees}
                                onChange={(e) => handleChange('attendees', e.target.value)}
                                sx={{ '& .MuiFilledInput-root': { bgcolor: '#f5f5f5', borderRadius: 1 } }}
                                InputProps={{ disableUnderline: true }}
                            />
                        </Box>

                        {/* Date and Time */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <AccessTimeIcon sx={{ color: '#616161', fontSize: 20 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                <TextField
                                    type="date"
                                    value={meetingData.startDate}
                                    onChange={(e) => handleChange('startDate', e.target.value)}
                                    variant="filled"
                                    size="small"
                                    sx={{ width: 150, '& .MuiFilledInput-root': { bgcolor: '#f5f5f5' } }}
                                    InputProps={{ disableUnderline: true }}
                                />
                                <Select
                                    value={meetingData.startTime}
                                    onChange={(e) => handleChange('startTime', e.target.value)}
                                    size="small"
                                    variant="standard"
                                    sx={{ width: 100 }}
                                >
                                    <MenuItem value="04:00 PM">04:00 PM</MenuItem>
                                    <MenuItem value="04:30 PM">04:30 PM</MenuItem>
                                    <MenuItem value="05:00 PM">05:00 PM</MenuItem>
                                    <MenuItem value="05:30 PM">05:30 PM</MenuItem>
                                </Select>
                                <Typography sx={{ mx: 1 }}>→</Typography>
                                <TextField
                                    type="date"
                                    value={meetingData.endDate}
                                    onChange={(e) => handleChange('endDate', e.target.value)}
                                    variant="filled"
                                    size="small"
                                    sx={{ width: 150, '& .MuiFilledInput-root': { bgcolor: '#f5f5f5' } }}
                                    InputProps={{ disableUnderline: true }}
                                />
                                <Select
                                    value={meetingData.endTime}
                                    onChange={(e) => handleChange('endTime', e.target.value)}
                                    size="small"
                                    variant="standard"
                                    sx={{ width: 100 }}
                                >
                                    <MenuItem value="05:00 PM">05:00 PM</MenuItem>
                                    <MenuItem value="05:30 PM">05:30 PM</MenuItem>
                                    <MenuItem value="06:00 PM">06:00 PM</MenuItem>
                                </Select>
                                <Typography variant="caption" sx={{ ml: 1, color: '#616161' }}>30m</Typography>
                                <FormControlLabel
                                    control={<Switch size="small" />}
                                    label={<Typography variant="body2">All day</Typography>}
                                    sx={{ ml: 2 }}
                                />
                            </Box>
                        </Box>

                        {/* Recurrence */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <RepeatIcon sx={{ color: '#616161', fontSize: 20 }} />
                            <Select defaultValue="none" variant="filled" size="small" sx={{ width: 200, '& .MuiFilledInput-root': { bgcolor: '#f5f5f5' } }} disableUnderline>
                                <MenuItem value="none">Does not repeat</MenuItem>
                            </Select>
                        </Box>

                        {/* Location */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <LocationOnIcon sx={{ color: '#616161', fontSize: 20 }} />
                            <TextField
                                fullWidth
                                placeholder="Add location"
                                variant="filled"
                                value={meetingData.location}
                                onChange={(e) => handleChange('location', e.target.value)}
                                sx={{ '& .MuiFilledInput-root': { bgcolor: '#f5f5f5', borderRadius: 1 } }}
                                InputProps={{ disableUnderline: true }}
                            />
                        </Box>

                        {/* Rich Text Editor Mockup */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <FormatListBulletedIcon sx={{ color: '#616161', mt: 1, fontSize: 20 }} />
                            <Box sx={{ flex: 1, border: '1px solid #edebe9', borderRadius: 1 }}>
                                <Box sx={{ p: 1, display: 'flex', gap: 1, borderBottom: '1px solid #edebe9', flexWrap: 'wrap' }}>
                                    <IconButton size="small"><FormatBoldIcon fontSize="small" /></IconButton>
                                    <IconButton size="small"><FormatItalicIcon fontSize="small" /></IconButton>
                                    <IconButton size="small"><FormatUnderlinedIcon fontSize="small" /></IconButton>
                                    <IconButton size="small"><StrikethroughSIcon fontSize="small" /></IconButton>
                                    <Divider orientation="vertical" flexItem />
                                    <IconButton size="small"><FormatQuoteIcon fontSize="small" /></IconButton>
                                    <IconButton size="small"><LinkIcon fontSize="small" /></IconButton>
                                    <IconButton size="small"><FormatListBulletedIcon fontSize="small" /></IconButton>
                                    <IconButton size="small"><FormatListNumberedIcon fontSize="small" /></IconButton>
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    placeholder="Type details for this new meeting"
                                    variant="standard"
                                    value={meetingData.details}
                                    onChange={(e) => handleChange('details', e.target.value)}
                                    InputProps={{ disableUnderline: true, style: { padding: '16px', fontSize: '14px' } }}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Box>

                {/* Right Sidebar - Meeting Options */}
                <Box sx={{ width: 300, borderLeft: '1px solid #edebe9', p: 3, display: { xs: 'none', lg: 'block' } }}>
                    <Stack spacing={4}>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#616161' }}>Who can bypass the lobby?</Typography>
                            <Select fullWidth size="small" defaultValue="people" variant="outlined" sx={{ borderRadius: 1 }}>
                                <MenuItem value="people">People in my org</MenuItem>
                            </Select>
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#616161' }}>Who can present</Typography>
                            <Select fullWidth size="small" defaultValue="everyone" variant="outlined" sx={{ borderRadius: 1 }}>
                                <MenuItem value="everyone">Everyone</MenuItem>
                            </Select>
                        </Box>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};

// Mock icon for title
const EditIcon = ({ sx }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={sx}>
        <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25Z" fill="currentColor" />
        <path d="M20.71 7.04C21.1 6.65 21.1 6.01 20.71 5.63L18.37 3.29C17.99 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor" />
    </svg>
);

export default ScheduleMeet;
